import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import bgImage from "../images/page-3-2.jpg";
import bgMobile from "../images/page-3-mobile.jpg";

const ORIGINAL_W = 6000;
const ORIGINAL_H = 3374;

// Desktop-Hotspots (px im Originalbild)
const emailRectPx = [3687, 2176, 4190, 2245];
const wwwRectPx = [4386, 2175, 4874, 2244];

// Desktop: Objekt-fit-cover → Prozentwerte
function rectForCoverToPercent([x1, y1, x2, y2], containerW, containerH) {
    const scale = Math.max(containerW / ORIGINAL_W, containerH / ORIGINAL_H);
    const renderedW = ORIGINAL_W * scale;
    const renderedH = ORIGINAL_H * scale;
    const offsetX = (containerW - renderedW) / 2;
    const offsetY = (containerH - renderedH) / 2;

    const rx1 = x1 * scale + offsetX;
    const ry1 = y1 * scale + offsetY;
    const rw = (x2 - x1) * scale;
    const rh = (y2 - y1) * scale;

    return {
        left: `${(rx1 / containerW) * 100}%`,
        top: `${(ry1 / containerH) * 100}%`,
        width: `${(rw / containerW) * 100}%`,
        height: `${(rh / containerH) * 100}%`,
    };
}

/* =======================
   MOBILE-Konstanten
======================= */
const MOB_W = 1536;
const MOB_H = 864;

// Feintuning-Offsets (Mail + WWW)
const MOBILE_OFFSETS_MAIL = { x: -25, y: 8, w: 0, h: 0 };
const MOBILE_OFFSETS_WWW = { x: -25, y: 8, w: 0, h: 0 };

// Desktop-PX → Prozent → Mobile-PX
const toPercentRect = ([x1, y1, x2, y2], W, H) => [
    x1 / W, y1 / H, x2 / W, y2 / H
];
const fromPercentToMobPx = ([px1, py1, px2, py2]) => ([
    px1 * MOB_W, py1 * MOB_H, px2 * MOB_W, py2 * MOB_H
]);

// Mobile-Startwerte aus Desktop-Koordinaten
const emailRectMobPx = fromPercentToMobPx(toPercentRect(emailRectPx, ORIGINAL_W, ORIGINAL_H));
const wwwRectMobPx = fromPercentToMobPx(toPercentRect(wwwRectPx, ORIGINAL_W, ORIGINAL_H));

const rectPxToRenderedPxMail = ([x1, y1, x2, y2], scale) => ({
    left: (x1 + MOBILE_OFFSETS_MAIL.x) * scale,
    top: (y1 + MOBILE_OFFSETS_MAIL.y) * scale,
    width: (x2 - x1) * scale,
    height: (y2 - y1) * scale,
});

const rectPxToRenderedPxWWW = ([x1, y1, x2, y2], scale) => ({
    left: (x1 + MOBILE_OFFSETS_WWW.x) * scale,
    top: (y1 + MOBILE_OFFSETS_WWW.y) * scale,
    width: (x2 - x1) * scale,
    height: (y2 - y1) * scale,
});

/* ======================================
   DESKTOP VIEW
====================================== */
function DesktopView() {
    const wrapperRef = useRef(null);
    const [emailBox, setEmailBox] = useState({});
    const [wwwBox, setWwwBox] = useState({});

    useLayoutEffect(() => {
        const recompute = () => {
            const el = wrapperRef.current;
            if (!el) return;
            const w = el.clientWidth;
            const h = el.clientHeight;
            setEmailBox(rectForCoverToPercent(emailRectPx, w, h));
            setWwwBox(rectForCoverToPercent(wwwRectPx, w, h));
        };

        recompute();
        let ro = new ResizeObserver(recompute);
        ro.observe(wrapperRef.current);
        return () => ro.disconnect();
    }, []);

    const stop = (e) => e.stopPropagation();

    return (
        <div
            ref={wrapperRef}
            style={{
                position: "relative",
                width: "100vw",
                height: "100vh",
                overflow: "hidden",
                cursor: "pointer",
            }}
        >
            <img
                src={bgImage}
                alt=""
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    pointerEvents: "none",
                }}
            />

            <a
                href="mailto:mail@tw-a.net"
                onClick={stop}
                style={{
                    position: "absolute",
                    ...emailBox,
                    display: "block",
                }}
                title="E-Mail an mail@tw-a.net"
            />

            <a
                href="https://tw-a.net"
                onClick={stop}
                style={{
                    position: "absolute",
                    ...wwwBox,
                    display: "block",
                }}
                target="_blank"
                rel="noopener noreferrer"
                title="www.tw-a.net besuchen"
            />
        </div>
    );
}

/* ======================================
   TABLET VIEW (wie Page1/Page2: horizontal scroll)
====================================== */
function TabletView() {
    const containerRef = useRef(null);
    const [state, setState] = useState({ w: 0, h: 0, scale: 1 });
    const [emailBox, setEmailBox] = useState({});
    const [wwwBox, setWwwBox] = useState({});

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const recompute = () => {
            const h = el.clientHeight;
            const scale = h / ORIGINAL_H;
            const w = Math.round(ORIGINAL_W * scale);

            setState({ w, h, scale });

            // Rechts starten (wie Page2)
            el.scrollLeft = Math.max(0, w - el.clientWidth);

            const conv = ([x1, y1, x2, y2]) => {
                const left = x1 * scale;
                const top = y1 * scale;
                const width = (x2 - x1) * scale;
                const height = (y2 - y1) * scale;
                return { left, top, width, height };
            };

            setEmailBox(conv(emailRectPx));
            setWwwBox(conv(wwwRectPx));
        };

        recompute();
        const ro = new ResizeObserver(recompute);
        ro.observe(el);

        return () => ro.disconnect();
    }, []);

    const stop = (e) => e.stopPropagation();

    return (
        <div
            ref={containerRef}
            style={{
                width: "100vw",
                height: "100vh",
                overflowX: "auto",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                position: "relative",
                background: "#000",
            }}
        >
            <div
                style={{
                    position: "relative",
                    width: `${state.w}px`,
                    height: "100%",
                }}
            >
                <img
                    src={bgImage}
                    alt=""
                    style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: "auto",
                        display: "block",
                        pointerEvents: "none",
                        userSelect: "none",
                    }}
                    draggable={false}
                />

                <a
                    href="mailto:mail@tw-a.net"
                    onClick={stop}
                    style={{
                        position: "absolute",
                        left: `${emailBox.left || 0}px`,
                        top: `${emailBox.top || 0}px`,
                        width: `${emailBox.width || 0}px`,
                        height: `${emailBox.height || 0}px`,
                        display: "block",
                    }}
                    title="E-Mail an mail@tw-a.net"
                />

                <a
                    href="https://tw-a.net"
                    onClick={stop}
                    style={{
                        position: "absolute",
                        left: `${wwwBox.left || 0}px`,
                        top: `${wwwBox.top || 0}px`,
                        width: `${wwwBox.width || 0}px`,
                        height: `${wwwBox.height || 0}px`,
                        display: "block",
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="www.tw-a.net besuchen"
                />
            </div>
        </div>
    );
}

/* ======================================
   MOBILE VIEW (dein bisheriger Code, gekapselt)
====================================== */
function MobileView() {
    const scrollRef = useRef(null);
    const imgRef = useRef(null);
    const [emailBoxMob, setEmailBoxMob] = useState(null);
    const [wwwBoxMob, setWwwBoxMob] = useState(null);

    useEffect(() => {
        const sc = scrollRef.current;
        const img = imgRef.current;
        if (!sc || !img) return;

        const compute = () => {
            const r = img.getBoundingClientRect();
            const scale = r.height / MOB_H;
            const renderedW = r.width;

            // Mail
            const b = rectPxToRenderedPxMail(emailRectMobPx, scale);
            setEmailBoxMob({
                position: "absolute",
                display: "block",
                left: (b.left + MOBILE_OFFSETS_MAIL.x) + "px",
                top: (b.top + MOBILE_OFFSETS_MAIL.y) + "px",
                width: (b.width + MOBILE_OFFSETS_MAIL.w) + "px",
                height: (b.height + MOBILE_OFFSETS_MAIL.h) + "px",
            });

            // WWW
            const w = rectPxToRenderedPxWWW(wwwRectMobPx, scale);
            setWwwBoxMob({
                position: "absolute",
                display: "block",
                left: (w.left + MOBILE_OFFSETS_WWW.x) + "px",
                top: (w.top + MOBILE_OFFSETS_WWW.y) + "px",
                width: (w.width + MOBILE_OFFSETS_WWW.w) + "px",
                height: (w.height + MOBILE_OFFSETS_WWW.h) + "px",
            });

            const SCROLL_OFFSET = 90;
            sc.scrollLeft = Math.max(0, renderedW - sc.clientWidth - SCROLL_OFFSET);
        };

        if (img.complete) compute();
        else img.addEventListener("load", compute, { once: true });

        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(compute);
            ro.observe(sc);
        } else {
            window.addEventListener("resize", compute);
        }

        return () => {
            if (img) img.removeEventListener?.("load", compute);
            if (ro) ro.disconnect();
            else window.removeEventListener("resize", compute);
        };
    }, []);

    const stop = (e) => e.stopPropagation();

    return (
        <div
            ref={scrollRef}
            style={{
                width: "100vw",
                height: "100vh",
                overflowX: "auto",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                touchAction: "auto",
                position: "relative",
            }}
        >
            <img
                ref={imgRef}
                src={bgMobile}
                alt=""
                style={{ height: "100%", width: "auto", display: "block" }}
            />

            {emailBoxMob && (
                <a
                    href="mailto:mail@tw-a.net"
                    style={emailBoxMob}
                    title="E-Mail an mail@tw-a.net"
                    onClick={stop}
                />
            )}

            {wwwBoxMob && (
                <a
                    href="https://tw-a.net"
                    style={wwwBoxMob}
                    title="www.tw-a.net besuchen"
                    onClick={stop}
                    target="_blank"
                    rel="noopener noreferrer"
                />
            )}
        </div>
    );
}

/* ======================================
   MAIN COMPONENT – VIEW SWITCH
====================================== */
export default function Page3() {
    const [vw, setVw] = useState(
        () => (typeof window !== "undefined" ? window.innerWidth : 1920)
    );

    useEffect(() => {
        const onResize = () => setVw(window.innerWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    if (vw < 768) return <MobileView />;
    if (vw <= 1024) return <TabletView />;
    return <DesktopView />;
}
